import { v7 } from 'uuid';

//internal deps
import { IThriveText, clearAndFocusEditor } from 'thrive-text';
import { IObservable, Observable } from "thrive-observable-react";

//services
import PollingService from "./Polling.Service";
import { CS } from "./Chat.Service";

//api
import { createNewChat as createNewChatApi, getChatById, getChatMessagesById, sendChatMessage } from "../api"

//interface
import { ChatType, IChatMessage, IChatMeta, IMessageBody, IUser } from "../interfaces";

//utils
import { CAS } from './ChatConfig.Service';

export class ChatConversationService {
    
    private pollForMessages!: PollingService;

    private readonly chatMessageUri: IObservable<string> = Observable<string>('');

    public chatUsers: IObservable<IUser[]> = Observable<IUser[]>([]);

    public chatMessages: IObservable<IChatMessage[]> = Observable<IChatMessage[]>([]);

    public chatId: IObservable<number> = Observable<number>(0);

    public isNewChat: IObservable<boolean> = Observable<boolean>(false);
    
    public newChatUserDetails: IObservable<IUser | null> = Observable<IUser | null>(null);

    public totalMessages: IObservable<number> = Observable<number>(0) 

    public skipCache: IObservable<number | undefined> = Observable<number | undefined>(undefined);

    public newChatIsLoading: IObservable<boolean> = Observable<boolean>(false);


    constructor(){
        // when chat id changes stop poller and get initial chats.
        this.chatId.subscribe((value: number) => {
            this?.pollForMessages?.endPoller?.()
            
            if(!this.isNewChat.value) {
                this.chatMessages.update([]);
            }
            
            if(value > 0 ){
                this.isNewChat.update(false);
                this.getInitialChats();
                this.startPollingForMessages();
                CS.hideNewChatPlaceholder();
                this.newChatUserDetails.update(null);
            }
            // id is 0 if a chat is a new chat
            if(value === 0) {
                this.isNewChat.update(true);
                this.endConversation();
                this?.pollForMessages?.endPoller?.()
            }
        });

        this.skipCache.subscribe((value: number | undefined) => {
            if(Number(value) > 0) {
                // clear the skip cache after 500 miliseconds
                setTimeout(() => this.skipCache.update(undefined), 500);
            }
        })

        this.isNewChat.subscribe((val: boolean) => {
            if(!val) {
                CS.hideNewChatPlaceholder();
            }
        })
    }

    public startPollingForMessages() : void {
        if(this.pollForMessages) {
            this.pollForMessages.endPoller();
        }

        this.pollForMessages = new PollingService();

        const pollerConfig = {
            request:  (ifModified: string) => getChatMessagesById(this.chatId.value, ifModified) as Promise<any>,
            onSuccess: (data: any) => {
                const newMessages = this.getNewMessages(data.items);
                if(newMessages.length) {
                    this.chatMessages.update(
                        [...newMessages, ...this.chatMessages.value]
                        .toSorted(this.sortListDescending)
                    )
                    this.totalMessages.update(data.totalItemCount)
                    CS.refreshChats();
                }
                
                
            },
            onError: (err:any) => console.error(err),
            config: {
                timeout: 2000,
                stopPollingOnError: false
            }
        }

        this.pollForMessages.startPoller(pollerConfig);
    }

    public getNewMessages(newMessages: IChatMessage[]){
        const chatMessageIds = this.chatMessages.value.slice(0, 20).map(m => m.id);

        return newMessages.filter(m => !chatMessageIds.includes(m.id)).map(m => ({...m, isSent: true, isNew:false}));
    }

    public setChatId(id: number) {
        this.chatId.update(id);
    }

    public setNewChatUserDetails(userDetails: IUser | null) {
        this.newChatUserDetails.update(userDetails);
    }

    public getInitialChats(){
        this.newChatIsLoading.update(true);
        CAS.setNameForPageTitle();
        getChatById(this.chatId.value)
        .then(({data}) =>  {
            this.chatUsers.update(data.users)

            // set page title
            CAS.setNameForPageTitle(data.users)
            
            if(data.id) {
                // update message uri for polling

                // get initial chat messages
                getChatMessagesById(data.id, '')
                .then(({data}) => {
                    this.totalMessages.update(data.totalItemCount);
                    this.chatMessages.update(data.items
                        .map((message:IChatMessage) => ({...message, isSent: true}))
                        .toSorted(this.sortListDescending)
                    );
                })
            }
        })
        .finally(() => {
            this.newChatIsLoading.update(false);
            this.pollForMessages.updateLastModified(new Date());
        })
    }

    public getUserFromSenderId(senderId: number){
        return this.chatUsers.value.find((user: any) => Number(user.id) === Number(senderId));
    }

    public senderIsMe(senderId: number) {
        return senderId === CS.me.value.id
    }

    private updateChatMessageAtId(id: string, newChatMessage: IChatMessage){
        this.chatMessages.update(this.chatMessages.value.map(m => {
            if(m.id === id) {
                return newChatMessage
            }
            return m
        }))
    }

    private formatChatMessage(message: {body: IMessageBody}): IChatMessage{
        const timeStampDate = new Date();

        return {
            id: v7(),
            timestamp: timeStampDate.toISOString(),
            senderId: CS.me.value.id,
            body: message.body,
            isSent: false,
            sentAt: timeStampDate.toISOString()
        }
    }

    public sendChatMessage(message: {body: IMessageBody}, editor?: IThriveText) {

        const newMessage:IChatMessage = this.formatChatMessage(message);

        this.chatMessages.update([newMessage, ...this.chatMessages.value]);
        
        this.postMessage(newMessage);

        if(editor) {
            clearAndFocusEditor(editor)
        };
    }

    public postMessage(newMessage: IChatMessage) {
        if(this.chatId.value) {
            sendChatMessage(this.chatId.value, newMessage)
            .then(() => {
                this.updateChatMessageAtId(newMessage.id, {...newMessage, isSent: true, sendingError: false})
            })
            .catch(() => {
                this.updateChatMessageAtId(newMessage.id, {...newMessage, sendingError: true})
            })
            .finally(() => {
                CS.refreshChats();
            })
        }
    }

    public removeMessage(messageId: string) {
        this.chatMessages.update(this.chatMessages.value.filter(m => m.id !== messageId));
    }

    public createNewChat(message: {body: IMessageBody}, editor?: IThriveText) {
        
        if(this?.newChatUserDetails?.value?.id) {
            const newMessage:IChatMessage = this.formatChatMessage(message)

            this.chatMessages.update([newMessage, ...this.chatMessages.value]);
        
            createNewChatApi({chatType: ChatType.DIRECTMESSAGE, message: newMessage, targetUserId: this.newChatUserDetails.value.id as number})
            .then(({data}: {data: IChatMeta}) => {
                this.updateChatMessageAtId(newMessage.id, {...newMessage, isSent: true, sendingError: false})
                this.setChatId(data.id);
            })
            .catch(() => {
                this.updateChatMessageAtId(newMessage.id, {...newMessage, sendingError: true})
            })
            .finally(() => {
                this.isNewChat.update(false);
                CS.refreshChats();
            })

            if(editor) {
                clearAndFocusEditor(editor)
                
            };
        }

        
    }

    public endConversation(){
        this?.pollForMessages?.endPoller?.();
        this.chatMessages.update([]);
        this.chatUsers.update([]);
        this.totalMessages.update(0)
    }

    

    public getNextPage(isAtTop: boolean){
        // if we already tried this skip, drop it
        if(this.skipCache.value === this.chatMessages.value.length) {
            return;
        }
         // otherwise get the next page
        if(this.totalMessages.value > this.chatMessages.value.length && isAtTop) {
            this.skipCache.update(this.chatMessages.value.length);
            getChatMessagesById(this.chatId.value, '', {skip: this.chatMessages.value.length})
            .then(({data}) => {
                this.chatMessages.update(
                    [
                        ...this.chatMessages.value,
                        ...data.items.map((message:IChatMessage) => ({...message, isSent: true}))
                    ].toSorted(this.sortListDescending)
                )
            })
        }
    }

    public resetService() {
        this.pollForMessages?.endPoller();
        this.chatMessageUri.update(0)
        this.chatUsers.update([]);
        this.chatMessages.update([]);
        this.chatId.update(0);
        this.isNewChat.update(false);
        this.totalMessages.update(0) ;
        this.skipCache.update(undefined); 
    }

    public handleMessageDirection(editor:IThriveText,  plaintextBody: {body: IMessageBody}) {
         if (this.chatId.value === 0) {
            ConversationService.createNewChat(plaintextBody, editor)
        }
        else {
            ConversationService.sendChatMessage(plaintextBody, editor);
        };
    }

    private sortListDescending(a:IChatMessage, b:IChatMessage) { 
        const timeA = new Date(a.sentAt).getTime()
        const timeB = new Date(b.sentAt).getTime()

        return timeB - timeA;
    }
}

export const ConversationService = new ChatConversationService();